import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { registerApplication, start } from 'single-spa';
import { canAccessEcm, canAccessSpectre, isEcmRoute, isSpectreRoute } from './context/AuthContext';

// Set global flag to indicate microfrontend mode for child apps
(window as any).__IS_MICROFRONTEND__ = true;

// Create and add the loader element to the DOM
const createLoader = () => {
  const loaderContainer = document.createElement('div');
  loaderContainer.id = 'mf-loader';
  loaderContainer.className = 'mf-loader-container hidden';
  loaderContainer.innerHTML = `
    <div class="mf-loader"></div>
    <span class="mf-loader-text">Loading...</span>
  `;
  document.body.appendChild(loaderContainer);
  return loaderContainer;
};

const loader = createLoader();

// Broadcast the loading state so React components (e.g. Header) can disable interactions
// while a microfrontend is being loaded. The flag on window covers components that mount
// after the event was already dispatched.
const setMicrofrontendLoading = (loading: boolean) => {
  (window as any).__MF_LOADING__ = loading;
  window.dispatchEvent(new CustomEvent('mf-loading-change', { detail: loading }));
};

setMicrofrontendLoading(false);

// Show loader when microfrontend starts loading
const showLoader = () => {
  loader.classList.remove('hidden');
  setMicrofrontendLoading(true);
};

// Hide loader when microfrontend finishes loading
const hideLoader = () => {
  loader.classList.add('hidden');
  setMicrofrontendLoading(false);
};

// Check if navigating to microfrontend root (not internal routes)
// Only show loader for initial load: /ecm, /ecm/, /spectre, /spectre/
const isMicrofrontendRoute = (path: string) => {
  return path === '/ecm' || path === '/ecm/' ||
    path === '/spectre' || path === '/spectre/';
};

// Show loader when navigating to microfrontend routes
window.addEventListener('single-spa:before-routing-event', () => {
  const path = window.location.pathname;
  if (isMicrofrontendRoute(path)) {
    showLoader();
  }
});

// Hide loader after routing completes (with small delay to ensure render)
window.addEventListener('single-spa:routing-event', () => {
  setTimeout(hideLoader, 200);
});

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// The `activeWhen` guards are the authoritative protection: they keep a microfrontend from ever
// being loaded or mounted for a user without the matching entitlement, even for the split second
// before the shell rewrites a forbidden URL. They read the session off `window.__USER_SESSION__`
// because single-spa evaluates them outside React; it is unset until the first session check
// resolves, which is why AuthContext dispatches `popstate` afterwards to force a re-evaluation.
registerApplication({
  name: 'ecm-client',
  app: () => (window as any).System.import('ecm-client'),
  activeWhen: (location) => {
    const session = (window as any).__USER_SESSION__;
    return isEcmRoute(location.pathname) && canAccessEcm(session);
  },
});

registerApplication({
  name: 'spectre-mf',
  app: () => (window as any).System.import('spectre-mf'),
  activeWhen: (location) => {
    const session = (window as any).__USER_SESSION__;
    return isSpectreRoute(location.pathname) && canAccessSpectre(session);
  },
});

start();

reportWebVitals();
